import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { API_CONFIG } from '../config/api';
import { useAuth } from '../contexts/AuthContext';
import '../styles/pages/_branch-detail.scss';
import Button from '../components/Button';
import { ArrowLeft, Building2, Users, Calendar, CheckCircle, Phone, MapPin, Shield } from 'lucide-react';
import { toast } from 'react-toastify';

interface Admin {
  id: string;
  name: string;
  email: string;
}

interface BranchDetail {
  id: string;
  name: string;
  email: string;
  createdAt: string;
  status: string;
  totalUsers: number;
  linkedDate?: string;
  parentEnterprise?: string;
  address?: string;
  phone?: string;
  admins?: Admin[];
}

const BranchDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [branch, setBranch] = useState<BranchDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [unlinking, setUnlinking] = useState(false);
  const [showUnlinkConfirm, setShowUnlinkConfirm] = useState(false);

  useEffect(() => {
    const fetchBranch = async () => {
      setLoading(true);
      setError('');
      try {
        if (!token) {
          throw new Error('Authentication required');
        }

        const response = await fetch(`${API_CONFIG.BASE_URL}/api/enterprise-hierarchy/branch/${id}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || 'Failed to fetch branch details');
        }

        const data = await response.json();
        if (!data.success || !data.data) {
          throw new Error('Invalid response from server');
        }
        setBranch(data.data);
      } catch (err: any) {
        setError(err.message || 'Failed to load branch details');
        console.error('Error fetching branch:', err);
      } finally {
        setLoading(false);
      }
    };
    if (id && token) fetchBranch();
  }, [id, token]);

  const handleUnlink = async () => {
    if (!id || !branch) return;

    setUnlinking(true);
    setError('');

    try {
      if (!token) {
        throw new Error('Authentication required');
      }

      const response = await fetch(`${API_CONFIG.BASE_URL}/api/enterprise-hierarchy/unlink-branch/${id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to unlink branch');
      }

      toast.success(`Branch "${branch.name}" successfully unlinked!`);
      setTimeout(() => navigate(-1), 1500);
    } catch (err: any) {
      const errorMsg = err.message || 'Failed to unlink branch';
      setError(errorMsg);
      toast.error(errorMsg);
      console.error('Error unlinking branch:', err);
    } finally {
      setUnlinking(false);
      setShowUnlinkConfirm(false);
    }
  };

  if (loading) {
    return (
      <div className="branch-detail">
        <div className="branch-detail__loading">
          <div className="branch-detail__spinner"></div>
          <p>Loading branch details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="branch-detail">
        <div className="branch-detail__error-container">
          <div className="branch-detail__error">
            <h3>Error Loading Branch</h3>
            <p>{error}</p>
            <div className="branch-detail__error-actions">
              <button
                onClick={() => navigate(-1)}
                className="branch-detail__btn branch-detail__btn--secondary"
              >
                <ArrowLeft size={16} /> Go Back
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!branch) {
    return (
      <div className="branch-detail">
        <div className="branch-detail__error-container">
          <div className="branch-detail__error">
            <h3>Branch Not Found</h3>
            <p>The requested branch could not be found.</p>
            <div className="branch-detail__error-actions">
              <button
                onClick={() => navigate(-1)}
                className="branch-detail__btn branch-detail__btn--secondary"
              >
                <ArrowLeft size={16} /> Go Back
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="branch-detail">
      <div className="branch-detail__container">
        {/* Header */}
        <div className="branch-detail__header-section">
          <button
            onClick={() => navigate(-1)}
            className="branch-detail__back-btn"
            aria-label="Go back"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="branch-detail__title">{branch.name}</h1>
            <p className="branch-detail__subtitle">Branch / Sub-Enterprise</p>
          </div>
        </div>

        {/* Main Info Card */}
        <section className="branch-detail__main-card">
          <div className="branch-detail__card-header">
            <div className="branch-detail__card-title-section">
              <Building2 size={24} className="branch-detail__card-icon" />
              <div>
                <h2 className="branch-detail__card-title">Branch Information</h2>
                <span className={`branch-detail__badge branch-detail__badge--${branch.status.toLowerCase()}`}>
                  <CheckCircle size={14} /> {branch.status}
                </span>
              </div>
            </div>
          </div>

          <div className="branch-detail__info-grid">
            <div className="branch-detail__info-item">
              <label className="branch-detail__label">BRANCH NAME</label>
              <div className="branch-detail__value">{branch.name}</div>
            </div>

            <div className="branch-detail__info-item">
              <label className="branch-detail__label">EMAIL ADDRESS</label>
              <div className="branch-detail__value">{branch.email}</div>
            </div>

            <div className="branch-detail__info-item">
              <label className="branch-detail__label">BRANCH ID</label>
              <div className="branch-detail__value branch-detail__value--monospace">{branch.id}</div>
            </div>

            <div className="branch-detail__info-item">
              <label className="branch-detail__label">CREATED DATE</label>
              <div className="branch-detail__value">
                <Calendar size={16} className="branch-detail__value-icon" />
                {new Date(branch.createdAt).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </div>
            </div>

            {branch.linkedDate && (
              <div className="branch-detail__info-item">
                <label className="branch-detail__label">LINKED DATE</label>
                <div className="branch-detail__value">
                  {new Date(branch.linkedDate).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </div>
              </div>
            )}

            <div className="branch-detail__info-item">
              <label className="branch-detail__label">TOTAL USERS</label>
              <div className="branch-detail__value">
                <Users size={16} className="branch-detail__value-icon" />
                {branch.totalUsers}
              </div>
            </div>

            {branch.parentEnterprise && (
              <div className="branch-detail__info-item">
                <label className="branch-detail__label">PARENT ENTERPRISE</label>
                <div className="branch-detail__value">{branch.parentEnterprise}</div>
              </div>
            )}

            {branch.address && (
              <div className="branch-detail__info-item">
                <label className="branch-detail__label">ADDRESS</label>
                <div className="branch-detail__value">
                  <MapPin size={16} className="branch-detail__value-icon" />
                  {branch.address}
                </div>
              </div>
            )}

            {branch.phone && (
              <div className="branch-detail__info-item">
                <label className="branch-detail__label">PHONE</label>
                <div className="branch-detail__value">
                  <Phone size={16} className="branch-detail__value-icon" />
                  {branch.phone}
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Admins Section */}
        {branch.admins && branch.admins.length > 0 && (
          <section className="branch-detail__admins-section">
            <div className="branch-detail__section-header">
              <h2 className="branch-detail__section-title">
                <Shield size={20} className="branch-detail__section-icon" />
                Branch Administrators
              </h2>
              <p className="branch-detail__section-subtitle">
                {branch.admins.length} admin{branch.admins.length !== 1 ? 's' : ''}
              </p>
            </div>

            <div className="branch-detail__admins-list">
              {branch.admins.map(admin => (
                <div key={admin.id} className="branch-detail__admin-card">
                  <div className="branch-detail__admin-info">
                    <div className="branch-detail__admin-name">{admin.name}</div>
                    <div className="branch-detail__admin-email">{admin.email}</div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Actions */}
        <div className="branch-detail__actions">
          <button
            onClick={() => navigate(-1)}
            className="branch-detail__btn branch-detail__btn--secondary"
            disabled={unlinking}
          >
            <ArrowLeft size={16} /> Back
          </button>
          <button
            onClick={() => setShowUnlinkConfirm(true)}
            className="branch-detail__btn branch-detail__btn--danger"
            disabled={unlinking}
          >
            Unlink Branch
          </button>
        </div>

        {/* Unlink Confirmation Modal */}
        {showUnlinkConfirm && (
          <div className="branch-detail__modal-overlay" onClick={() => setShowUnlinkConfirm(false)}>
            <div
              className="branch-detail__modal"
              onClick={e => e.stopPropagation()}
            >
              <h2 className="branch-detail__modal-title">Unlink Branch?</h2>
              <p className="branch-detail__modal-message">
                Are you sure you want to unlink <strong>{branch.name}</strong>? This action cannot be undone. The branch will remain in the system but will no longer be linked to your parent enterprise.
              </p>
              <div className="branch-detail__modal-actions">
                <button
                  onClick={() => setShowUnlinkConfirm(false)}
                  className="branch-detail__btn branch-detail__btn--secondary"
                  disabled={unlinking}
                >
                  Cancel
                </button>
                <button
                  onClick={handleUnlink}
                  className="branch-detail__btn branch-detail__btn--danger"
                  disabled={unlinking}
                >
                  {unlinking ? 'Unlinking...' : 'Unlink Branch'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BranchDetailPage;
